import React, { useEffect, useState, useCallback } from "react";
import { SpeedTestStatus, useSpeedTest } from "hooks/useSpeedTest";
import { useSelector, useDispatch } from "react-redux";
import { RootState } from "store/reducers";
import { SwitcherStreamSetting } from "@switcherstudio/switcher-api-client";
import { client } from "api/client";
import { useSwitcherSdk } from "hooks/useSwitcherSdk";
import { useNavigate } from "react-router-dom";
import { addNotification } from "store/notification/slice";
import { AppDispatch } from "store/store";
import { NotificationType } from "store/notification/types";
import { useTranslation } from "react-i18next";
import { useParams } from "hooks/useParams";
import styles from "components/speed-test/SpeedTest/index.module.scss";
import { convertBpsToMbps } from "helpers/resolution";

export const SpeedTestPage: React.FC = () => {
    const { channelSettingsId } = useParams();
    const { t } = useTranslation();
    const dispatch = useDispatch<AppDispatch>();
    const { userInfo } = useSelector((s: RootState) => s.user);
    const featureClaims = userInfo?.Claims?.Details?.FeatureClaims;
    const navigate = useNavigate();

    const { isInBrowser } = useSwitcherSdk();
    const { results, speedTestStatus } = useSpeedTest();
    const [channel, setChannel] = useState<SwitcherStreamSetting>({});

    useEffect(() => {
        // Get channel settings if id is provided
        async function run() {
            if (channelSettingsId) {
                try {
                    const channel =
                        await client.switcherStreamSettings_GetSwitcherStreamSetting(
                            channelSettingsId
                        );
                    setChannel(channel);
                } catch {
                    dispatch(
                        addNotification({
                            type: NotificationType.Danger,
                            message: t("platforms:channel-settings-error")
                        })
                    );
                }
            }
        }

        run();
    }, [dispatch, channelSettingsId, t]);

    useEffect(() => {
        // Patch settings based on speed test results
        function createPatch(
            width: number,
            height: number,
            videoBitRate: number,
            audioChannelCount: number,
            audioBitrate: number,
            audioSampleRate: number
        ) {
            return {
                "video-frame-width": width,
                "video-frame-height": height,
                "video-bit-rate": videoBitRate,
                "audio-channel-count": audioChannelCount,
                "audio-bit-rate": audioBitrate,
                "audio-sample-rate": audioSampleRate
            };
        }

        if (results) {
            const { upload } = results;
            let update = {};

            /**
             * The upload settings are pulled from the recommendation from Cloudflare: https://developers.cloudflare.com/stream/faq/#what-are-the-recommended-upload-settings-for-video-uploads
             */
            if (upload >= 8) {
                update = createPatch(1920, 1080, 6000000, 2, 128000, 48000);
            } else if (upload >= 4.8) {
                update = createPatch(1280, 720, 3200000, 2, 128000, 48000);
            } else if (upload >= 2.4) {
                update = createPatch(960, 540, 1200000, 2, 128000, 48000);
            } else if (upload >= 1) {
                update = createPatch(640, 360, 800000, 1, 64000, 48000);
            } else {
                update = createPatch(480, 270, 500000, 1, 64000, 48000);
            }

            // set results
            setChannel((prev) => ({ ...prev, ...update }));
        }
    }, [results, featureClaims]);

    const save = useCallback(async () => {
        window.webkit.messageHandlers.switcher.postMessage({
            action: "setProfile",
            profile: channel
        });
    }, [channel]);

    useEffect(() => {
        if (isInBrowser) {
            // This page is for the App's web view only, so redirect if in browser
            navigate("/scheduled-livestreams");
        }
    }, [isInBrowser, navigate]);

    return (
        <>
            <div className="row">
                <div className="col-sm-12">
                    <div className="alert alert-info text-center" role="alert">
                        {(!speedTestStatus ||
                            speedTestStatus === SpeedTestStatus.InProgress) && (
                            <span className={styles["loading-ellipsis"]}>
                                {t("speed-test:testing-upload")}
                            </span>
                        )}
                        {speedTestStatus === SpeedTestStatus.Completed &&
                            results && (
                                <>
                                    {t("speed-test:test-complete")}
                                    <br />
                                    <strong>
                                        {t("speed-test:upload")}:{" "}
                                        {convertBpsToMbps(results.upload)} Mbps
                                    </strong>
                                </>
                            )}
                    </div>
                    {speedTestStatus === SpeedTestStatus.Error &&
                        results?.error && (
                            <div
                                className="alert alert-danger text-center"
                                role="alert"
                            >
                                {results.error.message}
                            </div>
                        )}
                    <button
                        className="btn btn-primary btn-block"
                        onClick={save}
                        disabled={speedTestStatus !== SpeedTestStatus.Completed}
                    >
                        {t("buttons:save")}
                    </button>
                </div>
            </div>
        </>
    );
};
